import React, { PureComponent } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router';
import { withAuth } from '@jg/auth-react';
import PageCard from '@jg/page-card';
import queryString from 'query-string';
import { apiThunk } from '../../actions';
import {
  getCharity,
  getTeamName,
  getHeroImage,
  getFrpData,
  getShortName,
  getMembersListExcludingLinkedFRP,
  getNumberOfMembersFromTeamData,
  getEventName,
} from '../../selectors/apiSelectors';
import { getSelectedCause } from '../../selectors/appSelectors';
import { makePhotoUrl, makePhotoSquare, withTrack } from '../../utils';
import { IAppState } from '../../types/state.d';
import config from '../../config';
import {
  IJoinedWithNewFrp,
  IJoinedWithNewFrpMapStateToProps,
  IJoinedWithNewFrpDispatchToProps,
} from './JoinedWithNewFrp.d';
import TrackLink from '../../utils/trackLink';
import { getLocks } from '../../selectors/locksSelectors';

export class JoinedWithNewFrp extends PureComponent<IJoinedWithNewFrp> {
  shortName = '';

  constructor(props: IJoinedWithNewFrp) {
    super(props);
    const {
      history: {
        location: { search },
      },
    } = this.props;
    const parsed = queryString.parse(search);
    this.shortName = parsed.shortName;
  }

  componentDidMount() {
    const { fetchFrpData } = this.props;
    fetchFrpData(this.shortName);
  }

  onPageCardClick = () => {
    const { track } = this.props;
    track({
      action_name: 'link click',
      subtype: 'fundraising page',
      event: 'link click',
      event_value: 'fundraising page',
      page_section: 'join team > youre in the team',
    });
  };

  render() {
    const {
      t,
      charityData,
      heroImage,
      teamName,
      auth,
      frpData,
      shortName,
      existingMembersList,
      selectedCause,
      numberOfMembers,
      teamlocks: { charity: isCharityLocked },
      eventName,
    } = this.props;

    const accessToken = auth.getAccessToken();
    const myProfile = {
      picture:
        accessToken &&
        makePhotoUrl(makePhotoSquare(accessToken.profilePictureUrl)),
      id: accessToken ? accessToken.guid : '',
    };
    const membersList = [...existingMembersList, myProfile];
    const coverPhoto = makePhotoUrl(heroImage || config.defaultCoverPhoto);
    const yourFRPCharityName =
      (selectedCause && selectedCause.name) || charityData.name;

    return (
      <>
        <Helmet title={t('title.join.createFRP.success')} />
        <main className="jg-space-pbxl" data-qa="joined-with-new-FRP-page">
          <h1 className="jg-h1 jg-space-ptmd jg-space-mbml container--heading">
            <Trans i18nKey="content.joinedwithnewfrp.heading" />
          </h1>
          <section data-qa="pagecard-team">
            <h2 className="jg-h2 jg-space-mbxs">
              <Trans i18nKey="content.joinedwithnewfrp.teampage" />
            </h2>
            <PageCard
              coverPhoto={coverPhoto}
              pageName={teamName}
              charityName={
                isCharityLocked
                  ? charityData.name
                  : t([
                      'content.yourTeamPage.multiCharities',
                      'multiple charities',
                    ])
              }
              usersList={membersList}
              usersNumber={numberOfMembers}
              eventName={eventName}
              fundraisingForLabel={
                <Trans i18nKey="content.yourteampage.fundraisingfor" />
              }
              userLabel={<Trans i18nKey="content.yourteampage.member" />}
              usersLabel={<Trans i18nKey="content.yourteampage.members" />}
              eventLabel={<Trans i18nKey="content.yourteampage.event" />}
              link={`${config.basePageUri}${shortName}`}
            />
          </section>
          <section data-qa="pagecard-frp">
            <h2 className="jg-h2 jg-space-mbxs jg-space-mtml">
              <Trans i18nKey="content.joinedwithnewfrp.fundraisingpage" />
            </h2>
            <PageCard
              coverPhoto={coverPhoto}
              pageName={frpData ? frpData.name : ''}
              charityName={yourFRPCharityName}
              fundraisingForLabel={
                <Trans i18nKey="content.yourteampage.fundraisingfor" />
              }
              usersList={[myProfile]}
              pageOwner={
                accessToken &&
                `${accessToken.givenName} ${accessToken.familyName}`
              }
              link={`${config.baseFrpUri}/${this.shortName}`}
              onClick={this.onPageCardClick}
            />
          </section>
          <div className="jg-tc-button-container">
            <TrackLink
              href={`${config.baseFrpUri}/account/your-pages/${this.shortName}`}
              data-qa="personalise-fundraising-page"
              className="jg-btn jg-btn--brand jg-space-mtms"
              trackingData={{
                action_name: 'link click',
                subtype: 'button',
                event: 'link click',
                event_value: 'personalise fundraising page',
                page_section: 'join team > youre in the team',
              }}
            >
              <Trans i18nKey="content.joinedwithnewfrp.personalisefrp" />
            </TrackLink>
          </div>
        </main>
      </>
    );
  }
}

export const mapDispatchToProps: IJoinedWithNewFrpDispatchToProps = {
  fetchFrpData: apiThunk.fetchFrpData,
};

export const mapStateToProps = (
  state: IAppState,
): IJoinedWithNewFrpMapStateToProps => ({
  teamName: getTeamName(state),
  heroImage: getHeroImage(state),
  charityData: getCharity(state),
  eventName: getEventName(state),
  frpData: getFrpData(state),
  shortName: getShortName(state),
  existingMembersList: getMembersListExcludingLinkedFRP(state),
  numberOfMembers: getNumberOfMembersFromTeamData(state),
  selectedCause: getSelectedCause(state),
  teamlocks: getLocks(state),
});
export default withTranslation()(
  withAuth(
    withRouter(
      connect(
        mapStateToProps,
        mapDispatchToProps,
      )(withTrack(JoinedWithNewFrp)),
    ),
  ),
);
